import { useId, useState } from "react";
import { odmien } from "../../lib/polski.js";
import { ETYKIETY_RODZAJU } from "../../lib/jakoscWypowiedzi.js";
import type { UwagaWypowiedzi } from "../../lib/raportOceny.js";
import { Etykieta, ListaWariantow, tonRodzaju } from "../ocena/Wskazania.js";
import { IconStrzalkaDol } from "../icons/index.js";
import { Badge } from "../ui/Badge.js";
import { Collapse } from "../ui/Collapse.js";

/**
 * Wskazania przy dymku: to samo wskazanie z oceny, ktore karta "Ocena wg metodyki" pokazuje per mowca, ulozone
 * tuz przy wypowiedzi, z ktorej pochodzi. Dzieki temu powod stoi obok dowodu (zaznaczonego cytatu w dymku), a nie na
 * dole strony. W interfejsie to "wskazania" (jak w karcie, legendzie i pliku Word: CP-9), a w kodzie "uwagi".
 *
 * Kreska wskazania i plakietka rodzaju maja kolor oceny (decyzja autora metodyki z 29.10.2026): zloty przy mocnej
 * stronie, szary przy rzeczy do poprawy. Rodzaj zawsze niesie tez slowo w plakietce (ST-3).
 *
 * Zaznaczony cytat i wskazanie laczy LITERA (a, b, c), nie cyfra: jedynym numerem w interfejsie jest numer
 * wypowiedzi. Litera niesie sama tresc ("wskazanie a" dla czytnika ekranu). Najechanie myszka wyroznia pare w obie
 * strony (wskazanie i jego cytat w dymku). Fokus z klawiatury dziala w jedna strone: fokus na przelaczniku
 * wariantow wyroznia cytat, bo zaznaczony tekst nie jest elementem fokusowalnym, a wskazanie mocnej strony nie ma
 * zadnego innego elementu fokusowalnego.
 *
 * Wariantow jest do trzech na wskazanie, wiec stoja w zwinietym bloku: wypowiedz z kilkoma wskazaniami nie
 * rosnie o pol ekranu. Cytat powtarzamy tylko tam, gdzie nie jest zaznaczony w tekscie (niezweryfikowany albo
 * nachodzacy na inny): bez niego wskazanie mowiloby o czyms, czego nie widac w dymku.
 */

function Uwaga({
  uwaga,
  aktywna,
  onAktywny,
}: {
  uwaga: UwagaWypowiedzi;
  aktywna: boolean;
  onAktywny: (znacznik: string | null) => void;
}): React.JSX.Element {
  const [wariantyOtwarte, setWariantyOtwarte] = useState(false);
  const idWariantow = useId();
  const mocna = uwaga.rodzaj === "mocna-strona";
  const liczbaWariantow = uwaga.warianty.length;
  const bezPodkreslenia = uwaga.znacznik === null;

  return (
    <li
      className={`uwaga${aktywna ? " is-aktywna" : ""}`}
      data-rodzaj={uwaga.rodzaj}
      onMouseEnter={() => onAktywny(uwaga.znacznik)}
      onMouseLeave={() => onAktywny(null)}
      // Fokus z klawiatury (przelacznik wariantow) wyroznia cytat tak samo jak najechanie myszka.
      onFocus={() => onAktywny(uwaga.znacznik)}
      onBlur={() => onAktywny(null)}
    >
      <div className="uwaga__head">
        {uwaga.znacznik !== null && (
          <span className="uwaga__znacznik">
            <span className="tylko-dla-czytnika">Wskazanie </span>
            {uwaga.znacznik}
          </span>
        )}
        <Badge wariant={tonRodzaju(uwaga.rodzaj)} kropka>
          {ETYKIETY_RODZAJU[uwaga.rodzaj]}
        </Badge>
        {!uwaga.cytatZweryfikowany && <Badge>cytat niezweryfikowany</Badge>}
        <span className="uwaga__parametr">{uwaga.parametr}</span>
      </div>

      {bezPodkreslenia && uwaga.cytat && <blockquote className="uwaga__cytat">{uwaga.cytat}</blockquote>}
      {!uwaga.cytatZweryfikowany && uwaga.cytat && (
        <p className="wskazanie__uwaga">
          Nie znaleźliśmy tego cytatu w wypowiedzi słowo w słowo, więc nie zaznaczamy go w tekście.
        </p>
      )}
      {uwaga.nachodzi && (
        <p className="wskazanie__uwaga">Ten cytat nakłada się na inny, więc w tekście jest zaznaczony tylko jeden z nich.</p>
      )}

      {uwaga.uzasadnienie && (
        <p className="uwaga__opis">
          <Etykieta ton={tonRodzaju(uwaga.rodzaj)}>{mocna ? "Dlaczego to dobre: " : "Jak to obniża wpływ: "}</Etykieta>
          {uwaga.uzasadnienie}
        </p>
      )}
      {mocna && uwaga.jakRozwijac && (
        <p className="uwaga__opis">
          <Etykieta>Jak rozwijać: </Etykieta>
          {uwaga.jakRozwijac}
        </p>
      )}

      {!mocna &&
        (liczbaWariantow === 0 ? (
          <p className="wskazanie__uwaga">Model nie podał lepszego wariantu tej wypowiedzi.</p>
        ) : (
          <div className="uwaga__warianty">
            <button
              type="button"
              className="uwaga__przelacznik"
              aria-expanded={wariantyOtwarte}
              aria-controls={idWariantow}
              onClick={() => setWariantyOtwarte((o) => !o)}
            >
              <span>
                {odmien(liczbaWariantow, "Lepszy wariant", "Lepsze warianty", "Lepsze warianty")}
                {` (${liczbaWariantow})`}
                {/* Kazdy przelacznik ma inna nazwa na liscie przyciskow czytnika: wiadomo, ktorego wskazania dotyczy. */}
                <span className="tylko-dla-czytnika">
                  {uwaga.znacznik === null ? `, parametr ${uwaga.parametr}` : `, wskazanie ${uwaga.znacznik}`}
                </span>
              </span>
              <IconStrzalkaDol size={14} className="uwaga__strzalka" />
            </button>
            <Collapse otwarty={wariantyOtwarte} id={idWariantow}>
              <ListaWariantow warianty={uwaga.warianty} />
            </Collapse>
          </div>
        ))}
    </li>
  );
}

export function UwagiWypowiedzi({
  numer,
  uwagi,
  aktywnyZnacznik,
  onAktywny,
}: {
  /** Numer wypowiedzi (od 1), ten sam co w kolumnie po lewej i w karcie oceny. */
  numer: number;
  uwagi: readonly UwagaWypowiedzi[];
  /** Litera wskazania, nad ktorym jest kursor albo fokus (albo cytat, nad ktorym jest kursor). */
  aktywnyZnacznik: string | null;
  onAktywny: (znacznik: string | null) => void;
}): React.JSX.Element {
  return (
    <div className="msg__uwagi" role="group" aria-label={`Wskazania do wypowiedzi ${numer}`}>
      {/* role="list": list-style: none odbiera liscie jej semantyke w Safari/VoiceOver (jak w Rozmowa.tsx). */}
      <ol className="uwagi" role="list">
        {uwagi.map((u) => (
          <Uwaga uwaga={u} aktywna={u.znacznik !== null && u.znacznik === aktywnyZnacznik} onAktywny={onAktywny} key={u.id} />
        ))}
      </ol>
    </div>
  );
}
